import { Button, DatePicker, Flex, Form } from "antd";
import type { CustomModalProps } from "../../../../../../components/CustomModal";
import CustomModal from "../../../../../../components/CustomModal";
import {
  useAttendanceGenerateReportMutation,
  useGetAttendanceReportsStatisticsQuery,
} from "../../../../../../app/features/employee/emp-attendance/emp-attendance.api";
import useError from "../../../../../../hooks/useError";
import dayjs from "dayjs";

import { toast } from "../../../../../../lib/toast";
import { useNavigate } from "react-router";

type Props = {
  open: boolean;
} & CustomModalProps;

export default function GenerateReportModal({
  open,
  handleClose,
  ...rest
}: Props) {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const startDate = dayjs().startOf("month").format("YYYY-MM-DD");
  const endDate = dayjs().endOf("month").format("YYYY-MM-DD");

  // ----- Mutation API ----------
  const [
    attendanceGenerateReport,
    { error: generateError, isLoading: generateLoading },
  ] = useAttendanceGenerateReportMutation();

  // ------- Query API ---------
  const { data, error } = useGetAttendanceReportsStatisticsQuery({
    params: {
      startDate,
      endDate,
    },
  });

  // ------ Error Handeling ---------
  useError(generateError, "Attendance reports generate failed");
  useError(error, "Report statistics failed");

  const handleFinish = async (values: any) => {
    const newData = {
      reportName: "Monthly Attendance Report",
      startDate: values?.startDate
        ? dayjs(values.startDate).format("YYYY-MM-DD")
        : null,
      endDate: values?.endDate
        ? dayjs(values.endDate).format("YYYY-MM-DD")
        : null,
    };

    // if (!values?.name) return;
    try {
      await attendanceGenerateReport({
        ...newData,
      }).unwrap();
      toast("success", "Attendance report will be generate shortly");
      navigate(`/admin/employee/report`);
      form.resetFields();
      handleClose();
    } catch (_err) {
      // error handled by useError hook
    }
  };

  return (
    <>
      <CustomModal
        width={450}
        open={open}
        handleClose={() => {
          form.resetFields();
          handleClose();
        }}
        title={"Report Request"}
        {...rest}
      >
        <Form
          form={form}
          name="report-request"
          onFinish={handleFinish}
          layout="vertical"
        >
          {/* From Date */}
          <Form.Item
            label="From"
            name="startDate"
            rules={[{ required: true, message: "Please select from date" }]}
          >
            <DatePicker
              size="large"
              className="w-full"
              showToday={false}
              onChange={() => {
                // reset To date if From date changes
                form.setFieldsValue({ endDate: null });
              }}
            />
          </Form.Item>

          {/* To Date */}
          <Form.Item
            label="To"
            name="endDate"
            dependencies={["startDate"]}
            rules={[
              { required: true, message: "Please select to date" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue("startDate");

                  // if either value is empty, skip validation
                  if (!value || !startDate) {
                    return Promise.resolve();
                  }

                  // endDate must NOT be before startDate
                  if (value.isBefore(startDate, "day")) {
                    return Promise.reject(
                      new Error("To date cannot be earlier than From date")
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <DatePicker size="large" className="w-full" showToday={false} />
          </Form.Item>

          {/* Note */}
          <div className="text-center text-sm">
            <span className="text-white ">[ </span>
            <span className="text-rose-500 ">Note:</span>
            <span className="text-white">
              You already generated {data?.data?.COMPLETED}{" "}
              {data?.data?.COMPLETED === 1 ? "report" : "reports"} this month ]
            </span>
          </div>

          {/* Buttons */}
          <Flex align="center" justify="center" gap={16} className="mt-6">
            <Button
              size="middle"
              htmlType="button"
              className="w-full h-10 !border-teal-500 !text-teal-500 !bg-transparent !font-semibold"
              onClick={() => {
                form.resetFields();
                handleClose();
              }}
            >
              Cancel
            </Button>

            <Button
              size="middle"
              htmlType="submit"
              loading={generateLoading}
              className="w-full h-10 !bg-teal-500 !text-neutral-700 !border-none !font-semibold"
            >
              Confirm
            </Button>
          </Flex>
        </Form>
      </CustomModal>
    </>
  );
}
